import React, { useState } from 'react';

import api from 'api';

import { Tab } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';

import { useMountEffect, useProfile } from 'shared/hooks';
import useUserEdit from 'features/admin/hooks/useUserEdit';
import useGoBack from 'navigation/hooks/useGoBack';
import BackButton from 'navigation/components/BackButton/BackButton';
import UserForm from 'features/admin/components/UserForm';
import UserPermissionsTable from 'features/admin/components/UserPermissionsTable';
import { mapApi2Form } from 'features/admin/components/UserForm/utils';
import UserActionButton from 'features/admin/components/UserForm/UserActionButton';
import PageTitle from 'shared/components/layout/PageTitle/PageTitle';
import EditButton from 'shared/components/buttons/EditButton';
import LoadingSpinner from 'shared/components/feedback/LoadingSpinner/LoadingSpinner';
import { FORCE_CHANGE_PASSWORD } from 'utils/constants';

import {
  Box,
  FlexBoxColumn,
  FlexBoxColumnFull,
  FlexBoxAlign,
  FlexBoxSpaceBetween,
} from 'styles/layout';

import styled from '@emotion/styled';
import ActionButton from 'shared/components/buttons/ActionButton/ActionButton';

const FormButton = styled(ActionButton)`
  width: 120px;
`;
const UserDetailsPage = () => {
  const { goBack } = useGoBack();
  const { profileData } = useProfile();

  const [updatedPermissions, setUpdatedPermissions] = useState([]);
  const [reloadPermissions, setReloadPermissions] = useState(0);

  const {
    userId,
    userData,
    isEditMode,
    isProvider,
    enableEditMode,
    disableEditMode,
    fetchUser,
    onUserUpdate,
  } = useUserEdit();

  useMountEffect(fetchUser);

  const selfDetails = React.useMemo(
    () => userId === profileData.id,
    [profileData, userId],
  );

  const shouldInvite = React.useMemo(
    () => userData?.userAccountStatus === FORCE_CHANGE_PASSWORD,
    [userData?.userAccountStatus],
  );

  const initValues = React.useMemo(() => mapApi2Form(userData), [userData]);

  const tabs = React.useMemo(() => {
    const baseTabs = [
      {
        key: 'userDetails',
        label: 'User Details',
        render: () => (
          <FlexBoxColumnFull p={2}>
            <UserForm
              initValues={initValues}
              hideButtons
              disabled={!isEditMode}
              onSubmit={onUserUpdate}
              onCancel={disableEditMode}
              isExternalProvider={userData?.isExternalProvider}
            />
          </FlexBoxColumnFull>
        ),
      },
    ];

    if (isProvider) {
      baseTabs.push({
        key: 'accessPermissions',
        label: 'Access Permissions',
        render: () => (
          <UserPermissionsTable
            editMode={isEditMode}
            setUpdatedPermissions={setUpdatedPermissions}
            reloadTrigger={reloadPermissions}
            userData={userData}
            userId={userId}
          />
        ),
      });
    }

    return baseTabs;
  }, [
    initValues,
    userData,
    userId,
    isEditMode,
    disableEditMode,
    onUserUpdate,
    reloadPermissions,
    isProvider,
  ]);

  const [tab, setTab] = React.useState(tabs[0].key);

  return (
    <FlexBoxColumn height="100%">
      <FlexBoxSpaceBetween>
        <FlexBoxAlign>
          {!isEditMode && <BackButton onBack={goBack} />}
          <PageTitle>User Profile</PageTitle>
        </FlexBoxAlign>
        <FlexBoxAlign>
          {!selfDetails &&
            !!userData?.enabled &&
            !userData?.isExternalProvider &&
            shouldInvite && <UserActionButton userData={userData} />}
          {!isEditMode && !selfDetails && (
            <EditButton onClick={enableEditMode} />
          )}

          {isEditMode && !selfDetails && (
            <>
              {tab === tabs[0].key && (
                <Box ml={3.5} mr={3.5}>
                  <FormButton color="secondary" form="userForm">
                    Save
                  </FormButton>
                </Box>
              )}
              {tabs?.length>1 && tab === tabs[1].key && (
                <Box ml={3.5} mr={3.5}>
                  <FormButton
                    color="secondary"
                    onClick={async () => {
                      if (updatedPermissions.length === 0) return;
                      try {
                        await api.updateUserFacilityAccessPermission({
                          accessPermissions: updatedPermissions,
                        });
                        setUpdatedPermissions([]);
                        disableEditMode();
                        setReloadPermissions((prev) => prev + 1);
                      } catch (err) {
                        // console.error('Failed to update permissions', err);
                      }
                    }}
                  >
                    Save
                  </FormButton>
                </Box>
              )}
              <FormButton
                type="button"
                variant="outlined"
                color="secondary"
                onClick={disableEditMode}
              >
                Cancel
              </FormButton>
            </>
          )}
        </FlexBoxAlign>
      </FlexBoxSpaceBetween>
      <TabContext value={tab}>
        <FlexBoxAlign
          width="100%"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <TabList
            onChange={(_, newValue) => setTab(newValue)}
            aria-label="userDetails"
          >
            {tabs.map(({ key, label }) => (
              <Tab key={key} label={label} value={key} />
            ))}
          </TabList>
        </FlexBoxAlign>
        {!userData ? (
          <LoadingSpinner />
        ) : (
          tabs.map(({ key, render }) => (
            <TabPanel
              key={key}
              value={key}
              sx={{
                padding: 0,
                marginTop: 2,
              }}
            >
              {render()}
            </TabPanel>
          ))
        )}
      </TabContext>
    </FlexBoxColumn>
  );
};

export default UserDetailsPage;
